export function sentenceCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
}

export interface StatusPresentation { label: string; tone: 'success' | 'pending' | 'danger' | 'neutral' }
export type StatusOverrides = Record<string, Partial<StatusPresentation>>;
/** Plain wording for a leave portion code ("first_half" -> "First half"). */
export function leavePortionLabel(portion?: string | null): string {
  const key = (portion || '').trim().toLowerCase();
  if (key === 'full' || key === 'full_day') return 'Full day';
  if (key === 'first_half') return 'First half';
  if (key === 'second_half') return 'Second half';
  if (key === 'hours') return 'Hours';
  return key ? sentenceCase(key) : 'Partial day';
}

/**
 * Shift-swap statuses as stored (Requested, Pending, Approved, Declined, Rejected) and the
 * blank value legacy rows carry. Spread into V2StatusPill `overrides`.
 */
export const swapStatusOverrides: StatusOverrides = {
  requested: { label: 'Pending', tone: 'pending' }, pending: { label: 'Pending', tone: 'pending' },
  approved: { label: 'Approved', tone: 'success' },
  declined: { label: 'Declined', tone: 'danger' }, rejected: { label: 'Declined', tone: 'danger' },
  '': { label: '—', tone: 'neutral' }
};

export function statusPresentation(status: string | null | undefined, overrides: StatusOverrides = {}): StatusPresentation {
  const key = (status ?? '').trim().toLowerCase();
  const tone = ['active', 'approved', 'verified', 'current', 'paid', 'allow'].includes(key) ? 'success'
    : ['draft', 'pending_approval', 'proposed', 'pending', 'submitted', 'warn', 'escalate'].includes(key) ? 'pending'
    : ['blocked', 'rejected', 'terminated', 'expired_document', 'block'].includes(key) ? 'danger' : 'neutral';
  const known = ['active', 'approved', 'verified', 'current', 'draft', 'pending_approval', 'proposed', 'pending', 'blocked', 'rejected', 'terminated', 'expired', 'withdrawn', 'superseded', 'cancelled', 'inactive', 'paid', 'submitted', 'allow', 'warn', 'escalate', 'block', 'expired_document'];
  return { label: key === 'expired_document' ? 'Expired' : known.includes(key) ? sentenceCase(key) : 'Unknown', tone, ...overrides[key] };
}

export function vendorEntryAccess(input: { modules: string[]; recruitmentEnabled: boolean; recruitmentRead: boolean; payablesEnabled: boolean; payablesRead: boolean }): { portal: boolean; financial: boolean; canRead: boolean; source: 'payables' | 'recruitment' | null } {
  const portal = input.modules.includes('ONBOARDING') && input.recruitmentEnabled && input.recruitmentRead;
  const financial = input.modules.includes('PAYABLES') && input.payablesEnabled && input.payablesRead;
  return { portal, financial, canRead: portal || financial, source: financial ? 'payables' : portal ? 'recruitment' : null };
}

export function daysLeft(value?: string | null, today = new Date()): string | undefined {
  if (!value || formatDate(value) === 'Not available') return undefined;
  const civilToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((Date.parse(`${value.slice(0, 10)}T00:00:00Z`) - civilToday) / 86400000);
  return days < 0 ? `${Math.abs(days)} days ago` : days === 0 ? 'Ends today' : `${days} days left`;
}

// Decimal strings are formatted directly, without a floating-point money conversion.
export function formatMoney(value: string | number | null | undefined, currency: string): string {
  if (value === null || value === undefined || value === '') return 'Not available';
  const match = String(value).match(/^(-?)(\d+)(?:\.(\d{1,2}))?$/);
  if (!match || !/^[A-Z]{3}$/.test(currency || '')) return 'Not available';
  const [, sign, digits, decimal] = match;
  const formatter = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency, currencyDisplay: 'symbol', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const parts = formatter.formatToParts(BigInt(digits));
  const lastInteger = parts.map((part) => part.type).lastIndexOf('integer');
  const fraction = decimal && /[1-9]/.test(decimal) ? `.${decimal.padEnd(2, '0')}` : '';
  return sign + parts.map((part, index) => part.value + (index === lastInteger ? fraction : '')).join('');
}

export function formatDate(value?: string | null, style?: 'short' | 'weekday' | 'long' | 'time', timeZone?: string): string {
  if (!value) return style === 'time' ? 'Not recorded' : 'Open ended';
  const civil = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(civil)) return 'Not available';
  const date = new Date(`${civil}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== civil) return 'Not available';
  if (style === 'time') {
    if (!timeZone) return `${formatDate(civil, 'short')} ${value.slice(11, 16)}`.trim();
    const instant = new Date(value);
    if (!Number.isFinite(instant.getTime())) return 'Not available';
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone }).format(instant).replace(',', '');
  }
  if (style) return date.toLocaleDateString('en-GB', { day: 'numeric', month: style === 'long' ? 'long' : 'short', ...(style === 'weekday' || style === 'long' ? { weekday: style === 'long' ? 'long' : 'short' } as const : {}), ...(style === 'long' ? { year: 'numeric' } as const : {}), timeZone: 'UTC' });
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Civil shift times, independent of the browser's timezone. */
export function formatShiftRange({ start, end, date, endDayOffset, length = false, name }: { start?: string; end?: string; date?: string; endDayOffset?: number; length?: boolean; name?: string }): string {
  const clock = (value?: string) => value?.match(/(?:T|^)(\d{2}:\d{2})/)?.[1];
  const from = clock(start), to = clock(end);
  if (!from || !to) return name ? `${name} · Time not set` : 'No shift';
  const minutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3));
  // Inconsistent zero offsets crossing midnight, including equal clocks, mean a next-day end.
  const offset = Math.max(to <= from ? 1 : 0, endDayOffset ?? (start?.includes('T') && end?.includes('T') ? Math.round((Date.parse(`${end.slice(0, 10)}T00:00:00Z`) - Date.parse(`${start.slice(0, 10)}T00:00:00Z`)) / 86400000) : 0));
  const day = date || (start?.includes('T') ? start.slice(0, 10) : undefined);
  const weekday = (civil: string) => new Date(`${civil}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const next = day && new Date(Date.parse(`${day}T00:00:00Z`) + offset * 86400000).toISOString().slice(0, 10);
  const range = offset > 0 ? day ? `${weekday(day)} ${from} – ${weekday(next)} ${to}` : `${from} – ${to} overnight` : `${from} – ${to}`;
  const hours = (minutes(to) - minutes(from) + offset * 1440) / 60;
  return length ? `${range} · ${Number(hours.toFixed(2))} ${hours === 1 ? 'hour' : 'hours'}` : range;
}

export interface VendorTabAccess {
  modules: string[]; profile: boolean; portal: boolean; financial: boolean; banks: boolean;
  documents: boolean; bills: boolean; agreements: boolean;
}
export function visibleVendorTabs(access: VendorTabAccess): string[] {
  return [
    ...(access.profile ? ['profile'] : []),
    ...(access.modules.includes('ONBOARDING') && access.portal ? ['portal-access'] : []),
    ...(access.modules.includes('PAYABLES') ? [
      ...(access.financial ? ['financial'] : []), ...(access.banks ? ['bank-accounts'] : []),
      ...(access.documents ? ['documents'] : []), ...(access.bills ? ['bills-payments'] : [])
    ] : []),
    ...(access.modules.includes('AGREEMENTS') && access.agreements ? ['agreements'] : [])
  ];
}

export function vendorRedirect(pathname: string, search = ''): string {
  return pathname.replace(/^\/payables\/vendors(?=\/|$)/, '/vendors') + search;
}
